import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { getPatientMap } from "../../utils/services/Patients/getPatientMap";
import { getNewMembers } from "../../utils/services/APP/NewMember/getNewMembers";
import { useValidToken } from "../../hooks/useValidToken";

export const PatientMapContext = createContext();

export const PatientMapProvider = ({ children }) => {
  const [patientMap, setPatientMap] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isValid, authLoading } = useValidToken();

  // Paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Filtros
  const [locationId, setLocationId] = useState(null);
  const [memberType, setMemberType] = useState(null);
  const [pcpName, setPcpName] = useState(null);
  const [gender, setGender] = useState(null);
  const [ageFrom, setAgeFrom] = useState(null);
  const [ageTo, setAgeTo] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [memberFullName, setMemberFullName] = useState(null);
  const [filterAdmitted, setFilterAdmitted] = useState(null);
  const [filterDischarged, setFilterDischarged] = useState(null);
  const [filterEmergency, setFilterEmergency] = useState(null);
  const [filterHighCost, setFilterHighCost] = useState(null);
  const [filterHighRisk, setFilterHighRisk] = useState(null);

  // Ref para rastrear si ya hicimos el fetch inicial
  const initialFetchDone = useRef(false);

  // Función base reutilizable con useCallback
  const fetchWithOverrides = useCallback(async (overrides = {}) => {
    const filters = {
      page: page + 1,
      pageSize,
      locationId,
      memberType,
      pcpName,
      gender,
      ageFrom,
      ageTo,
      memberId,
      memberFullName,
      filterAdmitted,
      filterDischarged,
      filterEmergency,
      filterHighCost,
      filterHighRisk,
      ...overrides,
    };

    setLoading(true);
    try {
      const res = await getPatientMap(filters);
      setPatientMap(
        res.data.map((p) => ({
          ...p,
          id: p.cipId,
        }))
      );
      setTotal(res.total);
    } catch (err) {
      console.error("Error fetching patient map:", err);
    }
    setLoading(false);
  }, [
    page,
    pageSize,
    locationId,
    memberType,
    pcpName,
    gender,
    ageFrom,
    ageTo,
    memberId,
    memberFullName,
    filterAdmitted,
    filterDischarged,
    filterEmergency,
    filterHighCost,
    filterHighRisk,
  ]);

  const fetchData = useCallback(async () => {
    await fetchWithOverrides();
  }, [fetchWithOverrides]);

  const fetchNewMembers = useCallback(async () => {
    const filters = {
      page: page + 1,
      pageSize,
      locationId,
      memberType,
      pcpName,
      gender,
      ageFrom,
      ageTo,
      memberId,
      memberFullName,
      filterAdmitted,
      filterDischarged,
      filterEmergency,
      filterHighCost,
      filterHighRisk,
    };

    setLoading(true);
    try {
      const res = await getNewMembers(filters);
      setPatientMap(
        res.data.map((p) => ({
          ...p,
          id: p.cipId,
        }))
      );
      setTotal(res.total);
    } catch (err) {
      console.error("Error fetching new members:", err);
    }
    setLoading(false);
  }, [
    page,
    pageSize,
    locationId,
    memberType,
    pcpName,
    gender,
    ageFrom,
    ageTo,
    memberId,
    memberFullName,
    filterAdmitted,
    filterDischarged,
    filterEmergency,
    filterHighCost,
    filterHighRisk,
  ]);

  const fetchAcaAging62To65 = useCallback(async () => {
    await fetchWithOverrides({
      memberType: "ACA",
      ageFrom: 62,
      ageTo: 65,
    });
  }, [fetchWithOverrides]);

  const fetchAcaAging65Plus = useCallback(async () => {
    await fetchWithOverrides({
      memberType: "ACA",
      ageFrom: 65,
      ageTo: null,
    });
  }, [fetchWithOverrides]);

  // Fetch inicial solo una vez cuando el token es válido
  useEffect(() => {
    if (authLoading || !isValid) return;
    if (initialFetchDone.current) return;

    initialFetchDone.current = true;
    fetchData();
  }, [isValid, authLoading, fetchData]);

  return (
    <PatientMapContext.Provider
      value={{
        patientMap,
        total,
        loading,
        page,
        pageSize,
        setPage,
        setPageSize,

        // filtros
        locationId,
        setLocationId,
        memberType,
        setMemberType,
        pcpName,
        setPcpName,
        gender,
        setGender,
        ageFrom,
        setAgeFrom,
        ageTo,
        setAgeTo,
        memberId,
        setMemberId,
        memberFullName,
        setMemberFullName,
        filterAdmitted,
        setFilterAdmitted,
        filterDischarged,
        setFilterDischarged,
        filterEmergency,
        setFilterEmergency,
        filterHighCost,
        setFilterHighCost,
        filterHighRisk,
        setFilterHighRisk,

        // fetchers
        fetchData,
        fetchAcaAging62To65,
        fetchAcaAging65Plus,
        fetchNewMembers,
      }}
    >
      {children}
    </PatientMapContext.Provider>
  );
};
